import React, { useEffect, useState } from 'react';
import { labelAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
    Save,
    Loader2,
    Building2,
    Landmark,
    Youtube,
    Instagram,
    Music,
    Globe,
    Twitter,
    Image as ImageIcon,
    Phone,
    MapPin,
    User,
    Mail,
    FileText
} from 'lucide-react';

const SectionHeader = ({ icon: Icon, title, description }) => (
    <div className="flex items-start gap-3 pb-2">
        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
            <Icon className="w-4 h-4 text-primary" />
        </div>
        <div>
            <h3 className="font-heading font-semibold text-sm">{title}</h3>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
    </div>
);

const FieldRow = ({ label, children, className = '', locked = false }) => (
    <div className={`space-y-1.5 ${className}`}>
        <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {locked && (
                <span className="text-[9px] uppercase tracking-wider font-medium text-amber-500 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-[1px]">
                    Admin-managed
                </span>
            )}
        </div>
        {children}
    </div>
);

const LabelProfileEditor = () => {
    const { user } = useAuth();
    const [label, setLabel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [documentType, setDocumentType] = useState('other');
    const [documentFile, setDocumentFile] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);

    // Sensitive legal/financial fields — locked to read-only for label role.
    // Admin can still edit these via /admin/labels.
    const isLabelRole = user?.role === 'label';
    const SENSITIVE_FIELDS = [
        'bank_account_number', 'bank_ifsc_code', 'bank_account_name', 'bank_name',
        'pan_number', 'gst_number', 'tan_number',
        'registered_name', 'registered_address',
    ];
    const isSensitive = (field) => isLabelRole && SENSITIVE_FIELDS.includes(field);

    useEffect(() => {
        const fetchLabel = async () => {
            try {
                if (!user?.label_id) return;
                const res = await labelAPI.getOne(user.label_id);
                setLabel(res.data);
                setForm({
                    bio: res.data.bio || '',
                    phone: res.data.phone || '',
                    address: res.data.address || '',
                    registered_name: res.data.registered_name || '',
                    registered_address: res.data.registered_address || '',
                    pan_number: res.data.pan_number || '',
                    tan_number: res.data.tan_number || '',
                    gst_number: res.data.gst_number || '',
                    contact_person: res.data.contact_person || '',
                    contact_person_email: res.data.contact_person_email || '',
                    contact_person_phone: res.data.contact_person_phone || '',
                    bank_account_name: res.data.bank_account_name || '',
                    bank_account_number: res.data.bank_account_number || '',
                    bank_ifsc_code: res.data.bank_ifsc_code || '',
                    bank_account_type: res.data.bank_account_type || '',
                    bank_name: res.data.bank_name || '',
                    bank_branch: res.data.bank_branch || '',
                    profile_image: res.data.profile_image || '',
                    cover_image: res.data.cover_image || '',
                    website: res.data.website || '',
                    youtube_url: res.data.youtube_url || '',
                    instagram_url: res.data.instagram_url || '',
                    spotify_url: res.data.spotify_url || '',
                    twitter_url: res.data.twitter_url || '',
                    featured_video_url: res.data.featured_video_url || ''
                });
            } catch (err) {
                toast.error('Failed to load label profile');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchLabel();
    }, [user?.label_id]);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

const handleDocumentUpload = async () => {
    if (!documentFile) {
        toast.error('Please choose a document');
        return;
    }

    if (!user?.label_id) {
        toast.error('Label profile is not linked');
        return;
    }

    setUploadingDocument(true);

    try {
        await labelAPI.uploadDocument(
            user.label_id,
            documentType,
            documentFile
        );

        const refreshed = await labelAPI.getOne(user.label_id);
        setLabel(refreshed.data);

        setDocumentFile(null);
        setDocumentType('other');

        const fileInput = document.getElementById('label-document-file');
        if (fileInput) fileInput.value = '';

        toast.success('Document uploaded successfully');
    } catch (error) {
        toast.error(
            error.response?.data?.detail ||
            'Failed to upload document'
        );
    } finally {
        setUploadingDocument(false);
    }
};

  const handleDocumentDownload = async (doc) => {
    try {
        const response = await labelAPI.downloadDocument(
            label.id,
            doc.id
        );

        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');

        link.href = blobUrl;
        link.download = doc.filename || 'document';

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        toast.error(
            error.response?.data?.detail ||
            'Failed to download document'
        );
    }
};

  const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await labelAPI.updateSelf(form);
            setLabel(res.data);
            toast.success('Label profile updated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    if (!label) return null;

    return (
        <form onSubmit={handleSave} data-testid="label-profile-editor">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-primary" />
                                Label Profile
                            </CardTitle>
                            <CardDescription>
                                Manage your business, banking, and channel details — {label.name}
                            </CardDescription>
                        </div>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="save-label-profile-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save changes
                                </>
                            )}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLabelRole && (
                        <div
                            className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-500"
                            data-testid="label-readonly-banner"
                        >
                            <span className="font-semibold">Heads up:</span>
                            <span className="text-amber-500/90">
                                Legal &amp; financial fields (PAN, GST, TAN, Bank Account, IFSC, Registered Name &amp; Address) are tied to your signed agreements and can only be edited by an Admin. Reach out to Eyp Finance for any changes.
                            </span>
                        </div>
                    )}
                    <Tabs defaultValue="business" className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger>
                            <TabsTrigger value="banking" data-testid="tab-banking">Banking</TabsTrigger>
                            <TabsTrigger value="channels" data-testid="tab-channels">Channels</TabsTrigger>
                            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
                        </TabsList>

                        {/* BUSINESS */}
                        <TabsContent value="business" className="space-y-6 pt-6">
                            <SectionHeader
                                icon={Building2}
                                title="Company & Tax Details"
                                description="Legal entity & taxation identifiers used for invoicing"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldRow label="Registered Business Name" locked={isSensitive('registered_name')}>
                                    <Input
                                        value={form.registered_name}
                                        onChange={handleChange('registered_name')}
                                        placeholder="e.g. UR Debut Media Pvt Ltd"
                                        data-testid="input-registered-name"
                                        disabled={isSensitive('registered_name')}
                                        readOnly={isSensitive('registered_name')}
                                    />
                                </FieldRow>
                                <FieldRow label="Contact Phone">
                                    <Input
                                        value={form.phone}
                                        onChange={handleChange('phone')}
                                        placeholder="+91 98XXXXXXXX"
                                        data-testid="input-phone"
                                    />
                                </FieldRow>
                                <FieldRow label="GST Number" locked={isSensitive('gst_number')}>
                                    <Input
                                        value={form.gst_number}
                                        onChange={handleChange('gst_number')}
                                        placeholder="22AAAAA0000A1Z5"
                                        data-testid="input-gst"
                                        disabled={isSensitive('gst_number')}
                                        readOnly={isSensitive('gst_number')}
                                    />
                                </FieldRow>
                                <FieldRow label="PAN Number" locked={isSensitive('pan_number')}>
                                    <Input
                                        value={form.pan_number}
                                        onChange={handleChange('pan_number')}
                                        placeholder="ABCDE1234F"
                                        data-testid="input-pan"
                                        disabled={isSensitive('pan_number')}
                                        readOnly={isSensitive('pan_number')}
                                    />
                                </FieldRow>
                                <FieldRow label="TAN Number" locked={isSensitive('tan_number')}>
                                    <Input
                                        value={form.tan_number}
                                        onChange={handleChange('tan_number')}
                                        placeholder="ABCD12345E"
                                        data-testid="input-tan"
                                        disabled={isSensitive('tan_number')}
                                        readOnly={isSensitive('tan_number')}
                                    />
                                </FieldRow>
                                <FieldRow label="Operating Address">
                                    <Input
                                        value={form.address}
                                        onChange={handleChange('address')}
                                        placeholder="Office address"
                                        data-testid="input-address"
                                    />
                                </FieldRow>
                                <FieldRow label="Registered Address" className="md:col-span-2" locked={isSensitive('registered_address')}>
                                    <Textarea
                                        value={form.registered_address}
                                        onChange={handleChange('registered_address')}
                                        placeholder="Registered address for invoicing"
                                        rows={2}
                                        data-testid="input-registered-address"
                                        disabled={isSensitive('registered_address')}
                                        readOnly={isSensitive('registered_address')}
                                    />
                                </FieldRow>
                            </div>

                            <div className="border-t border-border/40 pt-6">
                                <SectionHeader
                                    icon={User}
                                    title="Primary Contact"
                                    description="Person we contact for label-related matters"
                                />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                    <FieldRow label="Contact Person">
                                        <Input
                                            value={form.contact_person}
                                            onChange={handleChange('contact_person')}
                                            placeholder="Full name"
                                            data-testid="input-contact-person"
                                        />
                                    </FieldRow>
                                    <FieldRow label="Contact Email">
                                        <Input
                                            type="email"
                                            value={form.contact_person_email}
                                            onChange={handleChange('contact_person_email')}
                                            placeholder="email@company.com"
                                            data-testid="input-contact-email"
                                        />
                                    </FieldRow>
                                    <FieldRow label="Contact Phone">
                                        <Input
                                            value={form.contact_person_phone}
                                            onChange={handleChange('contact_person_phone')}
                                            placeholder="+91 XXXXXXXXXX"
                                            data-testid="input-contact-phone"
                                        />
                                    </FieldRow>
                                </div>
                            </div>
                        </TabsContent>

                        {/* BANKING */}
                        <TabsContent value="banking" className="space-y-6 pt-6">
                            <SectionHeader
                                icon={Landmark}
                                title="Bank Account Details"
                                description="Used by Finance for payouts and invoice payments"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldRow label="Account Holder Name" locked={isSensitive('bank_account_name')}>
                                    <Input
                                        value={form.bank_account_name}
                                        onChange={handleChange('bank_account_name')}
                                        placeholder="Full name as per bank record"
                                        data-testid="input-bank-name"
                                        disabled={isSensitive('bank_account_name')}
                                        readOnly={isSensitive('bank_account_name')}
                                    />
                                </FieldRow>
                                <FieldRow label="Account Number" locked={isSensitive('bank_account_number')}>
                                    <Input
                                        value={form.bank_account_number}
                                        onChange={handleChange('bank_account_number')}
                                        placeholder="XXXXXXXXXXXXXX"
                                        data-testid="input-bank-number"
                                        disabled={isSensitive('bank_account_number')}
                                        readOnly={isSensitive('bank_account_number')}
                                    />
                                </FieldRow>
                                <FieldRow label="IFSC Code" locked={isSensitive('bank_ifsc_code')}>
                                    <Input
                                        value={form.bank_ifsc_code}
                                        onChange={handleChange('bank_ifsc_code')}
                                        placeholder="HDFC0001234"
                                        data-testid="input-bank-ifsc"
                                        disabled={isSensitive('bank_ifsc_code')}
                                        readOnly={isSensitive('bank_ifsc_code')}
                                    />
                                </FieldRow>
                                <FieldRow label="Account Type">
                                    <Input
                                        value={form.bank_account_type}
                                        onChange={handleChange('bank_account_type')}
                                        placeholder="Savings / Current"
                                        data-testid="input-bank-type"
                                    />
                                </FieldRow>
                                <FieldRow label="Bank Name" locked={isSensitive('bank_name')}>
                                    <Input
                                        value={form.bank_name}
                                        onChange={handleChange('bank_name')}
                                        placeholder="HDFC Bank"
                                        data-testid="input-bank-bank-name"
                                        disabled={isSensitive('bank_name')}
                                        readOnly={isSensitive('bank_name')}
                                    />
                                </FieldRow>
                                <FieldRow label="Branch">
                                    <Input
                                        value={form.bank_branch}
                                        onChange={handleChange('bank_branch')}
                                        placeholder="Branch name"
                                        data-testid="input-bank-branch"
                                    />
                                </FieldRow>
                            </div>
                        </TabsContent>

                        {/* CHANNELS */}
                        <TabsContent value="channels" className="space-y-6 pt-6">
                            <SectionHeader
                                icon={Globe}
                                title="Channels & Social"
                                description="Your public presence — shown on your label page"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldRow label="Website">
                                    <div className="relative">
                                        <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.website}
                                            onChange={handleChange('website')}
                                            placeholder="https://yourlabel.com"
                                            data-testid="input-website"
                                        />
                                    </div>
                                </FieldRow>
                                <FieldRow label="Instagram">
                                    <div className="relative">
                                        <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.instagram_url}
                                            onChange={handleChange('instagram_url')}
                                            placeholder="https://instagram.com/handle"
                                            data-testid="input-instagram"
                                        />
                                    </div>
                                </FieldRow>
                                <FieldRow label="YouTube">
                                    <div className="relative">
                                        <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.youtube_url}
                                            onChange={handleChange('youtube_url')}
                                            placeholder="https://youtube.com/@yourchannel"
                                            data-testid="input-youtube"
                                        />
                                    </div>
                                </FieldRow>
                                <FieldRow label="Spotify">
                                    <div className="relative">
                                        <Music className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.spotify_url}
                                            onChange={handleChange('spotify_url')}
                                            placeholder="https://open.spotify.com/..."
                                            data-testid="input-spotify"
                                        />
                                    </div>
                                </FieldRow>
                                <FieldRow label="Twitter / X">
                                    <div className="relative">
                                        <Twitter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.twitter_url}
                                            onChange={handleChange('twitter_url')}
                                            placeholder="https://x.com/handle"
                                            data-testid="input-twitter"
                                        />
                                    </div>
                                </FieldRow>
                                <FieldRow label="Featured Video URL">
                                    <div className="relative">
                                        <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            className="pl-9"
                                            value={form.featured_video_url}
                                            onChange={handleChange('featured_video_url')}
                                            placeholder="YouTube embed / video URL"
                                            data-testid="input-featured-video"
                                        />
                                    </div>
                                </FieldRow>
                            </div>
                        </TabsContent>

                        {/* BRANDING */}
                        <TabsContent value="branding" className="space-y-6 pt-6">
                            <SectionHeader
                                icon={ImageIcon}
                                title="Label Branding"
                                description="Logo, cover art, and short bio shown on your dashboard"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldRow label="Profile Image URL">
                                    <Input
                                        value={form.profile_image}
                                        onChange={handleChange('profile_image')}
                                        placeholder="https://..."
                                        data-testid="input-profile-image"
                                    />
                                </FieldRow>
                                <FieldRow label="Cover Image URL">
                                    <Input
                                        value={form.cover_image}
                                        onChange={handleChange('cover_image')}
                                        placeholder="https://..."
                                        data-testid="input-cover-image"
                                    />
                                </FieldRow>
                                <FieldRow label="Bio" className="md:col-span-2">
                                    <Textarea
                                        value={form.bio}
                                        onChange={handleChange('bio')}
                                        placeholder="Short description of your label (shown on the dashboard hero)"
                                        rows={4}
                                        data-testid="input-bio"
                                    />
                                </FieldRow>
                            </div>
                        </TabsContent>
                    </Tabs>
                    <div className="mt-8 border-t border-border/40 pt-6">
                        <SectionHeader
                            icon={FileText}
                            title="Documents"
                            description="Upload supporting documents for Finance/Admin review"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Document Type</Label>
                                <select
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                >
                                    <option value="pan_card">PAN Card</option>
                                    <option value="gst_certificate">GST Certificate</option>
                                    <option value="agreement">Agreement</option>
                                    <option value="bank_proof">Bank Proof</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label>Choose File</Label>
                                <Input
                                    id="label-document-file"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                                />
                            </div>

                            <Button
                                type="button"
                                onClick={handleDocumentUpload}
                                disabled={uploadingDocument || !documentFile}
                            >
                                {uploadingDocument ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-4 h-4 mr-2" />
                                        Upload Document
                                    </>
                                )}
                            </Button>
                        </div>

                        {label?.documents?.length > 0 && (
                            <div className="mt-6 space-y-3">
                                {label.documents.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="flex items-center justify-between rounded-lg border border-border/40 p-3"
                                    >
                                        <div>
                                            <p className="text-sm font-medium">
                                                {doc.filename}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {doc.document_type.replaceAll('_', ' ')}
                                                {doc.verified ? ' � Verified' : ' � Pending review'}
                                            </p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleDocumentDownload(doc)}
                                        >
                                            Download
                                        </Button>

                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </form>
    );
};

export default LabelProfileEditor;
